import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useCallback } from 'react';
import { canvasesApi } from '@/api/endpoints/canvases';
import type { CanvasSummary, VideoListResponse, VideoReference } from '@/api/types';
import { track } from '@/analytics/tracker';
import { isApiError } from '@/api/errors';
import { useToast } from '@/app/toasts/ToastProvider';
import { usePaywall } from '@/billing/PaywallContext';
import { t } from '@/i18n/i18n';
import { useAuth } from '@/auth/AuthProvider';

export const canvasesQueryKey = ['canvases'] as const;

/**
 * Имя служебной доски закладок. Saved живёт поверх обычной доски, и сервер
 * отдаёт её в общем списке — канвасом она не является ни в списке, ни в
 * счётчиках, ни в квоте (canvas-spec.md §8.1, имя не локализуется).
 */
export const SAVED_BOARD_NAME = 'Saved';

export function useCanvases() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: canvasesQueryKey,
    queryFn: () => canvasesApi.list(),
    enabled: isAuthenticated,
    select: (response) => response.data.filter((c) => c.name !== SAVED_BOARD_NAME),
  });
}

/** Сколько настоящих канвасов держит видео: id служебной доски не в счёт. */
export function canvasCountOf(
  video: Pick<VideoReference, 'saved_canvas_ids'>,
  canvases: readonly CanvasSummary[],
): number {
  return video.saved_canvas_ids.filter((id) => canvases.some((c) => c.id === id)).length;
}

export function useDefaultCanvas(): CanvasSummary | null {
  const { data } = useCanvases();
  return data?.find((c) => c.is_default) ?? data?.[0] ?? null;
}

export type SaveSource = 'grid' | 'tray' | 'sheet' | 'detail' | 'saved' | 'canvas';
export type CreateSource = 'catalog' | 'library';

type CanvasList = { data: CanvasSummary[] };

/**
 * Канвасы: сохранение из каталога (CNV-20/21), список (CNV-18). Карточки узнают
 * о канвасах через saved_canvas_ids — кеши выдачи и одиночного видео патчатся
 * сразу и помечаются устаревшими.
 */
export function useCanvasActions() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { openPaywall } = usePaywall();

  const patchVideos = useCallback(
    (videoIds: number[], canvasId: number, on: boolean) => {
      const patch = (video: VideoReference): VideoReference => {
        if (!videoIds.includes(video.id)) return video;
        const ids = on
          ? video.saved_canvas_ids.includes(canvasId)
            ? video.saved_canvas_ids
            : [...video.saved_canvas_ids, canvasId]
          : video.saved_canvas_ids.filter((id) => id !== canvasId);
        return { ...video, saved_canvas_ids: ids, is_saved: ids.length > 0 };
      };
      queryClient.setQueriesData<InfiniteData<VideoListResponse>>(
        { queryKey: ['videos'] },
        (data) =>
          data
            ? {
                ...data,
                pages: data.pages.map((page) => ({ ...page, data: page.data.map(patch) })),
              }
            : data,
      );
      queryClient.setQueriesData<VideoReference>({ queryKey: ['video'] }, (video) =>
        video ? patch(video) : video,
      );
      queryClient.setQueriesData<{ data: VideoReference[] }>(
        { queryKey: ['bookmarks', 'videos'] },
        (list) => (list ? { data: list.data.map(patch) } : list),
      );
      void queryClient.invalidateQueries({ queryKey: ['videos'], refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: canvasesQueryKey });
    },
    [queryClient],
  );

  const setList = useCallback(
    (updater: (list: CanvasSummary[]) => CanvasSummary[]) => {
      queryClient.setQueryData<CanvasList>(canvasesQueryKey, (prev) => ({
        data: updater(prev?.data ?? []),
      }));
    },
    [queryClient],
  );

  const addMutation = useMutation({
    mutationFn: ({
      canvasId,
      videoIds,
      searchEventId,
    }: {
      canvasId: number;
      videoIds: number[];
      searchEventId?: number | null;
      source: SaveSource;
    }) => canvasesApi.addVideos(canvasId, videoIds, searchEventId),
    onSuccess: (response, vars) => {
      patchVideos(vars.videoIds, vars.canvasId, true);
      for (const id of vars.videoIds) {
        track('reference_saved_to_canvas', {
          metadata: {
            canvas_id: vars.canvasId,
            video_reference_id: id,
            source: vars.source,
            search_event_id: vars.searchEventId ?? undefined,
          },
        });
      }
      toast.show(t('picker.addedTo', { name: response.data.name }));
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({
      canvasId,
      videoId,
    }: {
      canvasId: number;
      videoId: number;
      source: SaveSource;
    }) => canvasesApi.removeVideo(canvasId, videoId),
    onSuccess: (response, vars) => {
      patchVideos([vars.videoId], vars.canvasId, false);
      track('reference_removed_from_canvas', {
        metadata: {
          canvas_id: vars.canvasId,
          video_reference_id: vars.videoId,
          source: vars.source,
        },
      });
      toast.show(t('picker.removedFrom', { name: response.data.name }));
    },
  });

  const createMutation = useMutation({
    mutationFn: ({ name }: { name?: string; source: CreateSource }) => {
      // Имя по умолчанию локализуется здесь: без имени сервер даст английское «Untitled canvas N».
      const count = queryClient.getQueryData<CanvasList>(canvasesQueryKey)?.data.length ?? 0;
      return canvasesApi.create({ name: name ?? t('library.untitled', { n: count + 1 }) });
    },
    onSuccess: (response, vars) => {
      setList((list) => [response.data, ...list]);
      track('canvas_created', { metadata: { canvas_id: response.data.id, source: vars.source } });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'canvas_limit')
        openPaywall({ trigger: 'tripwire', source: 'canvas_limit', reason: 'canvas_limit' });
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string; oldName: string }) =>
      canvasesApi.rename(id, name),
    onSuccess: (response, vars) => {
      setList((list) => list.map((c) => (c.id === vars.id ? response.data : c)));
      track('canvas_renamed', {
        metadata: { canvas_id: vars.id, old_name: vars.oldName, new_name: vars.name },
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: number; itemsCount: number }) => canvasesApi.remove(id),
    onSuccess: (_response, vars) => {
      setList((list) => list.filter((c) => c.id !== vars.id));
      // Видео этого канваса теряют его в saved_canvas_ids — выдача и закладки перечитаются.
      void queryClient.invalidateQueries({ queryKey: ['videos'], refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: ['video'], refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      track('canvas_deleted', { metadata: { canvas_id: vars.id, items_count: vars.itemsCount } });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => canvasesApi.setDefault(id),
    onSuccess: (_response, id) => {
      setList((list) => list.map((c) => ({ ...c, is_default: c.id === id })));
      track('canvas_set_default', { metadata: { canvas_id: id } });
    },
  });

  const addToCanvas = useCallback(
    (canvasId: number, videoIds: number[], source: SaveSource, searchEventId?: number | null) =>
      addMutation.mutateAsync({ canvasId, videoIds, source, searchEventId }),
    [addMutation],
  );

  const removeFromCanvas = useCallback(
    (canvasId: number, videoId: number, source: SaveSource) =>
      removeMutation.mutateAsync({ canvasId, videoId, source }),
    [removeMutation],
  );

  /** «+ New canvas» в окне выбора: создать и сразу положить выбранное. */
  const createAndAdd = useCallback(
    async (videoIds: number[], source: SaveSource, searchEventId?: number | null) => {
      const created = await createMutation.mutateAsync({ source: 'catalog' });
      await addMutation.mutateAsync({ canvasId: created.data.id, videoIds, source, searchEventId });
      return created.data;
    },
    [createMutation, addMutation],
  );

  const createCanvas = useCallback(
    async (source: CreateSource, name?: string) =>
      (await createMutation.mutateAsync({ name, source })).data,
    [createMutation],
  );
  const renameCanvas = useCallback(
    (id: number, name: string, oldName: string) =>
      renameMutation.mutateAsync({ id, name, oldName }),
    [renameMutation],
  );
  const deleteCanvas = useCallback(
    (id: number, itemsCount: number) => deleteMutation.mutateAsync({ id, itemsCount }),
    [deleteMutation],
  );
  const setDefaultCanvas = useCallback(
    (id: number) => setDefaultMutation.mutateAsync(id),
    [setDefaultMutation],
  );

  return {
    addToCanvas,
    removeFromCanvas,
    createAndAdd,
    createCanvas,
    renameCanvas,
    deleteCanvas,
    setDefaultCanvas,
    isPending: addMutation.isPending || createMutation.isPending,
  };
}
